import { ComponentProps, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, MutationDefinition } from '@reduxjs/toolkit/dist/query'
import { MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks'
import cn from 'classnames'
import { BaseSelect } from 'components/ui/selects/BaseSelect'
import { getIsAddressDetailed } from 'helpers/address'
import { SetterVoidReturn } from 'helpers/types'
import { useIsMobile } from 'hooks/media'
import { DebouncedFunc } from 'lodash'
import { TAddress } from 'modules/customer/api'
import { TCustomerAddress } from 'modules/customer/types'

import { SelectAddressInput } from './SelectAddressInput'
import { SelectAddressOption } from './SelectAddressOption'

import s from './SelectAddress.module.less'

export type TDefaultInputProps = {
  onChange: SetterVoidReturn<string>
  placeholder: string
  searchValue: string
  autoFocus?: boolean
}

type TProps = {
  address: TAddress[] | undefined | null
  daDataSearchAddressValue: string
  debouncedGetAddress: DebouncedFunc<
    MutationTrigger<
      MutationDefinition<
        string,
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
        never,
        TAddress[] | null,
        'api'
      >
    >
  >
  initialValue: string | TCustomerAddress | undefined
  isAddressLoading: boolean
  searchAddressValue: string
  setDaDataSearchAddressValue: SetterVoidReturn<string>
  setErrorAddress: SetterVoidReturn<string>
  setSearchAddressValue: SetterVoidReturn<string>
  errorAddress?: string
  isCourier?: boolean | undefined
  isErrorAddress?: boolean
  name?: string
  placeholder?: string
  setIsErrorAddress?: SetterVoidReturn<boolean>
  setSelectedOption?: SetterVoidReturn<TAddress>
}

export const SelectAddress = memo(function SelectAddress({
  name,
  initialValue,
  errorAddress,
  setErrorAddress,
  address,
  isAddressLoading,
  searchAddressValue,
  setSearchAddressValue,
  daDataSearchAddressValue,
  setDaDataSearchAddressValue,
  debouncedGetAddress,
  placeholder,
  setSelectedOption,
  isErrorAddress,
  setIsErrorAddress,
  isCourier
}: TProps) {
  const [isDropDownOpen, setDropDownOpen] = useState(false)

  const MIN_QUERY_LENGTH = 3

  const isMobile = useIsMobile()

  useEffect(() => {
    if (daDataSearchAddressValue && debouncedGetAddress && daDataSearchAddressValue !== initialValue) {
      if (daDataSearchAddressValue.length >= MIN_QUERY_LENGTH) {
        debouncedGetAddress(daDataSearchAddressValue)
      }
    }
  }, [daDataSearchAddressValue, debouncedGetAddress, initialValue])

  useEffect(() => {
    if ((isCourier || isCourier === undefined) && daDataSearchAddressValue === initialValue && debouncedGetAddress) {
      debouncedGetAddress(daDataSearchAddressValue)
    }
    // eslint-disable-next-line
  }, [daDataSearchAddressValue])

  useEffect(() => {
    setSearchAddressValue(initialValue as string)
  }, [initialValue]) // eslint-disable-line

  useEffect(() => {
    if (address && initialValue !== daDataSearchAddressValue) {
      setDropDownOpen(true)
    }
  }, [address, daDataSearchAddressValue, initialValue])

  const handleOpen = useCallback((isDropDownOpen: boolean) => {
    setDropDownOpen(isDropDownOpen)
  }, [])

  const handleChange = (e: TAddress[]) => {
    const isDetailedAddress = getIsAddressDetailed(e[0].fiasLevel)

    if (e[0]?.value) {
      setSearchAddressValue(e[0].value)
      setDropDownOpen(false)

      setSelectedOption && setSelectedOption(e[0])

      if (isDetailedAddress) {
        searchAddressValue = e[0].value // eslint-disable-line
        setErrorAddress('')
        setIsErrorAddress ? setIsErrorAddress(true) : ''
      }

      if (!isDetailedAddress && isErrorAddress) {
        if (isCourier) {
          setErrorAddress('Уточните адрес')
        }
      }
    }
  }

  const onInputChange = (value: string) => {
    setSearchAddressValue(value)
    setDaDataSearchAddressValue(value)
    if (value.length === 0) {
      if (isCourier) {
        setErrorAddress('Уточните адрес')
      }
    }
  }

  const defaultInputProps = useMemo<TDefaultInputProps>(
    () => ({
      placeholder: placeholder || '',
      searchValue: searchAddressValue,
      onChange: onInputChange,
      autoFocus: true
    }),
    [searchAddressValue] // eslint-disable-line
  )

  const classNames = useMemo<ComponentProps<typeof BaseSelect>['classNames']>(
    () => ({
      select: { wrapper: s.wrapper, innerWrapper: cn({ [s.innerWrapper_inactive]: !isDropDownOpen }, s.searchInput) },
      option: { noOptionMessageText: s.noOptionMessageText }
    }),
    [isDropDownOpen]
  )

  return (
    <BaseSelect
      // @ts-ignore
      CustomOptionComponent={SelectAddressOption}
      // TODO убрать
      // @ts-ignore
      CustomSelectInput={SelectAddressInput}
      additionalInputProps={defaultInputProps}
      classNames={classNames}
      dataName={name}
      error={!!errorAddress}
      errorLabel={errorAddress}
      errorTestId="customers-page--add-new-customer-form--company-address-error"
      hasIcon={false}
      isLoading={isAddressLoading}
      isMobile={isMobile}
      isOpen={isDropDownOpen && !!address?.length}
      mobileName="Поиск заявок"
      name={name}
      // @ts-ignore
      onChange={handleChange}
      onSetOpen={handleOpen}
      // @ts-ignore
      options={address || []}
      placeholder={placeholder}
      showLoadingMessage={false}
      size="lg"
    />
  )
})
